import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useState } from 'react';

import { CustomSkillsManageView, type CustomSkillsLayout } from './custom-skills-manage-view';
import type { CustomSkillRecord } from '../../../../queries/chats';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { CreateSkillView } from '../custom-skills-ui';
import { SettingsCard } from '../settings-card';
import { useSettingsPanelContextSafe } from '../settings-panel-context';

export function CustomSkillsSettings({ layout = 'card' }: { layout?: CustomSkillsLayout }) {
  const settingsPanel = useSettingsPanelContextSafe();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const [localView, setLocalView] = useState<{ type: 'create' } | { type: 'edit'; skill: CustomSkillRecord } | null>(
    null,
  );

  const closeLocalView = () => {
    setLocalView(null);
  };

  const openCreateView = () => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ADD_SKILL_CLICKED, {
      chatUuid: chatOverlayContext?.chatUuid,
      source: 'settings',
    });

    if (settingsPanel) {
      settingsPanel.pushView({ type: 'custom-skill-create' });
    } else {
      setLocalView({ type: 'create' });
    }
  };

  const openUpdateView = (skill: CustomSkillRecord) => {
    if (settingsPanel) {
      settingsPanel.pushView({ type: 'custom-skill-edit', skillUuid: skill.uuid });
    } else {
      setLocalView({ type: 'edit', skill });
    }
  };

  if (localView) {
    return (
      <div className="flex min-h-[480px] min-w-0 flex-1 flex-col overflow-hidden rounded-[6px] border border-[#DEE2E7] bg-white">
        <CreateSkillView
          onBack={closeLocalView}
          skillToUpdate={localView.type === 'edit' ? localView.skill : undefined}
        />
      </div>
    );
  }

  if (layout === 'page') {
    return <CustomSkillsManageView layout="page" onAddSkill={openCreateView} onEditSkill={openUpdateView} />;
  }

  return (
    <SettingsCard
      title="Custom skills"
      action={
        <button
          type="button"
          onClick={openCreateView}
          className="text-brand-400 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold"
        >
          Add new skill
        </button>
      }
      flushContent
    >
      <CustomSkillsManageView layout="card" onAddSkill={openCreateView} onEditSkill={openUpdateView} />
    </SettingsCard>
  );
}
